import { isEqual } from "lodash"
import React, { useEffect, useRef, useState } from "react"
import { renderToStaticMarkup } from "react-dom/server"
import { v4 } from "uuid"
import { cls } from "../../../../Shared/cls"
import style from "./MultiStateSwitch.module.css"

type Option<K, V> = {
	key: K
	value: V
}

export type Props<K, V> = {
	options: Option<K, V>[]
	onSelect: (option: Option<K, V>, initial: boolean) => void
	selectedValue?: K | null
	disabled?: boolean
	wrapperClass?: string
	/**
	 * Whether this component should shrink in height and font-size on smaller screens (width <= 710px)
	 */
	enableSmallerSizeOnSmallerScreens?: boolean
}
export function MultiStateSwitch<K extends string, V>(props: Props<K, V>) {
	const id = useRef(v4())

	const [options, setOptions] = useState(props.options)
	const [selectedOption, setSelectedOption] = useState<K | null>(props.selectedValue || options[0]?.key || null)
	const [disabled, setDisabled] = useState(false)

	useEffect(() => {
		if (options[0]) {
			props.onSelect(options[0], true)
		}
	}, [])

	useEffect(() => {
		setDisabled(props.disabled !== undefined ? props.disabled : false)
	}, [props.disabled])

	useEffect(() => {
		if (props.selectedValue === undefined) {
			return
		}
		setSelectedOption(props.selectedValue)
	}, [props.selectedValue])

	useEffect(() => {
		if (!isEqual(props.options, options)) {
			setOptions(props.options)
		}
	}, [props.options])

	const moveAmount = props.enableSmallerSizeOnSmallerScreens ? 35 : 50

	useEffect(() => {
		document.head.insertAdjacentHTML(
			"beforeend",
			renderToStaticMarkup(
				<style
					className={`multiStateSwitch_${id.current}_style`}
					dangerouslySetInnerHTML={{
						__html: `
						${options
							.map((option, index) => {
								if (index === 0) {
									return `
									.multiStateSwitch_${id.current}:has(#switch_option_${option.key}_${id.current}:checked)::before {
										left: 5px;
									}
									`
								} else if (index === options.length - 1) {
									return `
									.multiStateSwitch_${id.current}:has(#switch_option_${option.key}_${id.current}:checked)::before {
										left: calc(${(100 / options.length) * index}% - 5px);
									}
									`
								} else {
									return `
									.multiStateSwitch_${id.current}:has(#switch_option_${option.key}_${id.current}:checked)::before {
										left: calc(${(100 / options.length) * index}%);
									}
									`
								}
							})
							.join("\n")}
						.multiStateSwitch_${id.current}::before {
							 width: ${options.length === 1 ? "calc(100% - 10px)" : 100 / options.length + "%"};
						}

						.multiStateSwitch_${id.current} label {
							 width: ${100 / options.length - 2}%;
							 max-width: ${100 / options.length - 2}%;
						}

						@container switchWrapper (max-width: ${getSwitchColumnBreakpoint(options.length)}) {
							.multiStateSwitch_${id.current} {
								flex-direction: column;
								height: unset !important;
								border-radius: 6px;
							}

							.multiStateSwitch_${id.current}::before {
								transition: 0.5s top ease;
								height: ${moveAmount}px;
								border-radius: 6px;
								width: calc(100% - 10px) !important;
							}

							.multiStateSwitch_${id.current} label {
								width: 100% !important;
								${props.enableSmallerSizeOnSmallerScreens ? "height: 35px;" : ""}
								max-width: unset !important;
								justify-content: flex-start;
							}

							${options
								.map((option, index) => {
									if (index === 0) {
										return `
									.multiStateSwitch_${id.current}:has(#switch_option_${option.key}_${id.current}:checked)::before {
										left: unset;
										top: 5px
									}
									`
									} else if (index === options.length - 1) {
										return `
									.multiStateSwitch_${id.current}:has(#switch_option_${option.key}_${id.current}:checked)::before {
										left: unset;
										top: calc(${moveAmount * (options.length - 1)}px + 5px);
									}
									`
									} else {
										return `
									.multiStateSwitch_${id.current}:has(#switch_option_${option.key}_${id.current}:checked)::before {
										left: unset;
										top: calc(${moveAmount * index}px + 5px);
									}
									`
									}
								})
								.join("\n")}
						}

						${
							props.enableSmallerSizeOnSmallerScreens
								? `
								@media (max-width: 710px) {
									.multiStateSwitch_${id.current} {
										height: 35px;
									}

									.multiStateSwitch_${id.current} label {
										padding: 8px;
										font-size: 14px;
									}
								}
							`
								: ""
						}
							`,
					}}></style>,
			),
		)

		const _id = id.current

		return () => {
			const elements = document.getElementsByClassName(`multiStateSwitch_${_id}_style`)
			Array.from(elements).forEach((x) => x.remove())
		}
	}, [options])

	function getSwitchColumnBreakpoint(optionAmount: number): string {
		if (optionAmount < 3) {
			return "280px"
		}

		return "400px"
	}

	return (
		<div className={cls(style.wrapper, props.wrapperClass)}>
			<div className={cls(style.switch, { [style.disabled]: disabled }, `multiStateSwitch_${id.current}`)}>
				{options.map((option, index) => {
					const checked = selectedOption === option.key
					return (
						<React.Fragment key={id.current + "_wrapper" + index + (selectedOption || "")}>
							<input
								disabled={disabled}
								type="radio"
								id={`switch_option_${option.key}_${id.current}`}
								name={`multiStateSwitch_${id.current}`}
								defaultChecked={checked}
								readOnly={true}
								value={option.key}
								onClick={(e) => {
									if (disabled) {
										return
									}
									setSelectedOption(option.key)
									props.onSelect(option, false)
								}}
							/>
							<label
								htmlFor={`switch_option_${option.key}_${id.current}`}
								data-selected={selectedOption === option.key}
								title={String(option.value)}>
								<span className={"oneLineClamp"}>{String(option.value)}</span>
							</label>
						</React.Fragment>
					)
				})}
			</div>
		</div>
	)
}
